// API Configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
export const API_VERSION = import.meta.env.VITE_API_VERSION || 'v2'

// Dynamic API base path functions that check runtime config
export const getApiBasePath = () => {
  const baseUrl = window.RUNTIME_CONFIG?.API_BASE_URL || APP_CONFIG.API_BASE_URL || API_BASE_URL
  const version = window.RUNTIME_CONFIG?.API_VERSION || APP_CONFIG.API_VERSION || API_VERSION

  return import.meta.env.DEV
    ? `/meetings/api/${version}`
    : `${baseUrl}/meetings/api/${version}`
}

export const getSubmissionsApiBasePath = () => {
  const baseUrl = window.RUNTIME_CONFIG?.API_BASE_URL || APP_CONFIG.API_BASE_URL || API_BASE_URL
  const version = window.RUNTIME_CONFIG?.API_VERSION || APP_CONFIG.API_VERSION || API_VERSION

  return import.meta.env.DEV
    ? `/submissions/api/${version}`
    : `${baseUrl}/submissions/api/${version}`
}

// Static constants for backward compatibility
// In development, use relative path for Vite proxy. In production, use full URL.
export const API_BASE_PATH = import.meta.env.DEV
  ? `/meetings/api/${API_VERSION}`
  : `${API_BASE_URL}/meetings/api/${API_VERSION}`

// New base path for the submissions service
export const SUBMISSIONS_API_BASE_PATH = import.meta.env.DEV
  ? `/submissions/api/${API_VERSION}`
  : `${API_BASE_URL}/submissions/api/${API_VERSION}`

// API Endpoints (updated for v2 structure)
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
  },

  // Forms (v2 API)
  FORMS: '/forms',
  FORM_BY_SLUG: (slug: string) => `/forms/${slug}`,

  // Slots (v2 API)
  SLOTS: '/slots',
  AVAILABLE_SLOTS: '/slots/available',

  // Bookings (v2 API)
  BOOKINGS: '/bookings',

  // Legacy v1 endpoints (if needed)
  LEGACY: {
    MEETING_TYPES: '/meeting-types',
    FORM_BY_CONTEXT: '/forms/get-by-context',
  },

  // Admin endpoints
  ADMIN: {
    USERS: '/admin/users',
    BOOKINGS: '/admin/bookings',
    AVAILABILITY: '/admin/availability',
    SLOT_OVERRIDES: '/admin/slot-overrides',
  },
} as const

// Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'tutlo_auth_token',
  USER_DATA: 'tutlo_user_data',
  THEME_PREFERENCE: 'tutlo_theme',
  LANGUAGE: 'tutlo_language',
  BOOKING_DRAFT: 'tutlo_booking_draft',
} as const

// Runtime config type declaration
declare global {
  interface Window {
    RUNTIME_CONFIG: {
      API_BASE_URL: string;
      API_VERSION: string;
      ENVIRONMENT: string;
      DEBUG_MODE: string;
    }
  }
}

// Application Configuration
export const APP_CONFIG = {
  NAME: import.meta.env.VITE_APP_NAME || 'Tutlo Meeting Scheduler',
  VERSION: import.meta.env.VITE_APP_VERSION || '1.0.0',
  ENVIRONMENT: window.RUNTIME_CONFIG?.ENVIRONMENT || import.meta.env.VITE_APP_ENVIRONMENT || 'development',
  API_BASE_URL: window.RUNTIME_CONFIG?.API_BASE_URL || import.meta.env.VITE_API_BASE_URL,
  API_VERSION: window.RUNTIME_CONFIG?.API_VERSION || import.meta.env.VITE_API_VERSION || 'v2',
  DEFAULT_LANGUAGE: 'en',
  DEFAULT_TIMEZONE: import.meta.env.VITE_DEFAULT_TIMEZONE || 'Europe/Warsaw',
  SLOT_DURATION_MINUTES: 30,
  MAX_BOOKING_DAYS_AHEAD: 90,
  MIN_BOOKING_HOURS_AHEAD: 2,
} as const

// Form Configuration
export const FORM_CONFIG = {
  VALIDATION_CACHE_TTL: parseInt(import.meta.env.VITE_CACHE_FORM_VALIDATION_TTL) || 5 * 60 * 1000, // 5 minutes
  MAX_RETRY_ATTEMPTS: parseInt(import.meta.env.VITE_MAX_FORM_RETRY_ATTEMPTS) || 3,
} as const

// Theme Configuration
export const THEME_CONFIG = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
} as const

// Tutlo Color Palette (matching Figma design spec)
export const TUTLO_COLORS = {
  PRIMARY: {
    MAIN: '#55CAFF',
    LIGHT: '#A0B8F0',
    DARK: '#1F4FCF',
    CONTRAST: '#FFFFFF'
  },
  ACCENT: {
    MAIN: '#FFC535',
    LIGHT: '#FFD66C',
    DARK: '#F5B014',
    CONTRAST: '#333333'
  },
  ERROR: {
    MAIN: '#E85645',
    LIGHT: '#FF7573',
    DARK: '#B01919',
    CONTRAST: '#FFFFFF'
  },
  SUCCESS: {
    MAIN: '#38BC3C',
    LIGHT: '#4CAF50',
    DARK: '#2E7D32',
    CONTRAST: '#FFFFFF'
  },
  WARNING: {
    MAIN: '#FFC227',
    LIGHT: '#FFD66C',
    DARK: '#F57F17',
    CONTRAST: '#333333'
  },
  INFO: {
    MAIN: '#2B5A0B',
    LIGHT: '#5FB8F9',
    DARK: '#0C387F',
    CONTRAST: '#FFFFFF'
  },
  // Supporting brand colors
  ORANGE: '#FF7C41',
  GREEN: '#38BC3C',
  FOCUS: '#FB98FA',
  // Neutral colors
  BLACK: '#333333',
  WHITE: '#FFFFFF',
  GREY: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121'
  }
} as const

// User Roles
export const USER_ROLES = {
  SYSTEM_ADMINISTRATOR: 'SystemAdministrator',
  SALES_ADMINISTRATOR: 'SalesAdministrator',
  FORM_ADMINISTRATOR: 'FormAdministrator',
} as const

// Notification Configuration
export const NOTIFICATION_CONFIG = {
  DEFAULT_TIMEOUT: 5000,
  ERROR_TIMEOUT: 8000,
  SUCCESS_TIMEOUT: 3000,
} as const

// Validation Rules
export const VALIDATION_RULES = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_REGEX: /^[\+]?[1-9][\d]{0,15}$/,
  MIN_PASSWORD_LENGTH: 8,
  MAX_NAME_LENGTH: 100,
  MAX_MESSAGE_LENGTH: 1000,
} as const

// Date/Time Formats
export const DATE_FORMATS = {
  DISPLAY: 'DD/MM/YYYY',
  API: 'YYYY-MM-DD',
  DATETIME_DISPLAY: 'DD/MM/YYYY HH:mm',
  DATETIME_API: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
  TIME_DISPLAY: 'HH:mm',
} as const

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
} as const

// Feature Flags (for development)
export const FEATURE_FLAGS = {
  ENABLE_DARK_MODE: import.meta.env.VITE_ENABLE_DARK_MODE === 'true',
  ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  ENABLE_NOTIFICATIONS: import.meta.env.VITE_ENABLE_NOTIFICATIONS !== 'false', // Default true
  ENABLE_OFFLINE_MODE: false,
  ENABLE_DEBUG_MODE: import.meta.env.DEV || window.RUNTIME_CONFIG?.DEBUG_MODE === 'true' || import.meta.env.VITE_ENABLE_DEBUG_MODE === 'true',
} as const

// Navigation Routes
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  BOOK: '/book',
  BOOKING_SUCCESS: '/booking/success',
  ADMIN: '/admin',
  ADMIN_BOOKINGS: '/admin/bookings',
  ADMIN_MEETING_TYPES: '/admin/meeting-types',
  ADMIN_FORMS: '/admin/forms',
  ADMIN_AVAILABILITY: '/admin/availability',
  PROFILE: '/profile',
  NOT_FOUND: '/not-found',
  ERROR: '/error',
} as const
